import React, { FC, useState } from 'react';
import Card from 'components/Card';
import { OptionCardContainer } from 'components/OptionCard/style';
import { GreenCheckIconNew, RedCloseIcon } from 'assets/icons';
import { useChangeBookingRoomType } from 'customHooks/useChangeBookingRoomType';
import { MoneyIcon, CityView, Vompire } from 'assets/icons';
import { AccountContentContainer } from './style';
import { colors } from 'constants/colors';
import Checkbox from 'components/Checkbox';
import StyledButton from 'components/Button';
import { useLoadTrips } from 'customHooks/useLoadTrips';
import { useHistory, useParams } from 'react-router-dom';
import { useGetAvailableRoomsByInput } from 'customHooks/useGetAvailableRoomsByInput';
import { RoomType } from 'types/commonTypes';
import { useGlobalAlerts } from 'customHooks/useGlobalAlerts'
import { useEffect, useMemo } from 'react';
import OptionCardModifyRoomType from 'components/OptionCardModifyRoomType';
import { ROOM_TYPE_OPTIONS } from 'constants/roomTypeOptions';
import { roomPairs } from 'constants/roomTypeOptions';
import { calcModifyRoomTypePreview } from "utils/calcModifyRoomTypePreview";
import { useCanChangeGroupRoomType } from 'customHooks/useCanChangeGroupRoomType';


const ModifyRoomType: FC = () => {
  const debug = false;


  const { showGlobalError, showGlobalMessage } = useGlobalAlerts()
  const history = useHistory();
  const { trips, loading } = useLoadTrips();

  const { groupId } = useParams<{ groupId: string }>();
  const { bookingId } = useParams<{ bookingId: string }>();
  const { roomId } = useParams<{ roomId: string }>();

  const trip = trips?.[groupId];

  if (debug) {
    console.log('(ModifyRoomType) trip: ', trip);
  }

  const booking = trip?.bookings?.find(b => b.bookingId === bookingId) || null;

  if (debug) {
    console.log('(ModifyRoomType) Current booking: ', booking);
  }


  const currentRoomTypeFromBooking = booking?.roomBookings?.[0]?.room?.roomType ?? null;

  if (debug) {
    console.log('(ModifyRoomType) currentRoomTypeFromBooking: ', currentRoomTypeFromBooking);
  }


  const currentRoomBooking =
    booking?.roomBookings?.find(rb => rb.room?.roomId === roomId) ||
    booking?.roomBookings?.[0] ||
    null;

  if (debug) {
    console.log('(ModifyRoomType) currentRoomBooking: ', currentRoomBooking);
  }

  const pickRoomBookingsToCount = (booking: any) => {
    const notDeleted = (booking?.roomBookings || []).filter((rb: any) => !rb?.isDeleted);
    const current = notDeleted.filter((rb: any) => rb?.isCurrent);

    if (current.length > 0) return current;
    return notDeleted.length > 0 ? [notDeleted[notDeleted.length - 1]] : [];
  };

  const getBookingRoomsPerNight = (booking: any) => {
    const rbs = pickRoomBookingsToCount(booking);
    return rbs.reduce((sum: number, rb: any) => {
      const v = Number(rb?.pricePerNight ?? 0);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
  };

  const currentPricePerNight = booking ? getBookingRoomsPerNight(booking) : 0;

  if (debug) {
    console.log('(ModifyRoomType) currentPricePerNight: ', currentPricePerNight);
  }

  const currentRoomTypeAsRoomType = useMemo<RoomType | null>(() => currentRoomTypeFromBooking
    ? ({
      roomTypeId: currentRoomTypeFromBooking.roomTypeId,
      name: currentRoomTypeFromBooking.name,
      bed: currentRoomTypeFromBooking.bed,
      hasWindows: currentRoomTypeFromBooking.hasWindows,
      capacity: currentRoomTypeFromBooking.capacity,
      pricePerNight: currentPricePerNight ?? 0,
    } as RoomType)
    : null, [currentRoomTypeFromBooking, currentPricePerNight]);

  if (debug) {
    console.log('(ModifyRoomType) currentRoomTypeAsRoomType: ', currentRoomTypeAsRoomType);
  }



  const currentRoomTypeId = currentRoomTypeAsRoomType?.roomTypeId;

  if (debug) {
    console.log('(ModifyRoomType) currentRoomTypeId: ', currentRoomTypeId);
  }

  const [selectedRoomType, setSelectedRoomType] = useState<RoomType | null>(null);

  const [applyThisChangeToAllRooms, setApplyThisChangeToAllRooms] = useState(false);


  const handleCancel = () => history.goBack();

  const { changeRoomType, loading: changingRoomType } = useChangeBookingRoomType();

  const handleRoomTypeUpdate = async () => {
    try {
      if (!booking || !selectedRoomType) return;

      // Step 1 → if there are sub-options, go to step 2
      if (step === 'ROOM_TYPE' && hasRoomOptions) {
        setStep('ROOM_OPTIONS');
        return;
      }

      const finalRoomTypeId =
        step === 'ROOM_OPTIONS'
          ? (selectedRoomOption?.roomTypeId ?? selectedRoomType.roomTypeId)
          : selectedRoomType.roomTypeId;

      if (!finalRoomTypeId) return;

      // If user selected the current room type — do nothing
      if (finalRoomTypeId === currentRoomTypeId) return;

      // 1) Apply to all active bookings in the group
      if (applyThisChangeToAllRooms) {
        await changeRoomType({
          groupId,
          newRoomTypeId: finalRoomTypeId,
        });

        showGlobalMessage('Room type was updated for all rooms in this booking.');
        history.goBack();
        return;
      }

      // 2) Change only this booking (or selected bookings later)
      await changeRoomType({
        bookings: [
          {
            bookingId,
            newRoomTypeId: finalRoomTypeId,
          },
        ],
      });

      showGlobalMessage('Room type was updated.');
      history.goBack();
    } catch (e: any) {
      showGlobalError(e?.message ?? 'Failed to update room type.');
    }
  };




  const guestsFromBooking =
    booking?.roomBookings?.[0]?.room?.roomType?.capacity ||
    1;

  const availabilityInput = booking
    ? {
      checkInDate: booking.checkInDate,
      checkOutDate: booking.checkOutDate,
      isWheelchairAccessible: booking.isWheelchairAccessible,
      roomQuantity: 1,
      guestQuantity: guestsFromBooking,
    }
    : null;


  if (debug) {
    console.log('(ModifyRoomType) availabilityInput:', availabilityInput);
  }

  const {
    searchResult: availableRoomTypes,
    loading: loadingRoomTypes,
    noAvailableRooms,
  } = useGetAvailableRoomsByInput(availabilityInput);

  if (debug) {
    console.log('(ModifyRoomType) availableRoomTypes from GET_AVAILABLE_ROOM_TYPES_BY_DATE_RANGE:', availableRoomTypes);
  }


  // if (debug) {
  //   console.log('availabilityInput:', availabilityInput);
  //   console.log('availableRoomTypes:', availableRoomTypes);
  //   console.log('noAvailableRooms:', noAvailableRooms);
  //   console.log('availableRoomsError:', availableRoomsError);
  // }


  const removeSpecificObject = (
    arr: RoomType[],
    searchArray: { searchValue: string; valuesToRemove: string[] }[]
  ) => {
    const newArray = [...arr];
    searchArray.forEach((item) => {
      const index = newArray.findIndex((obj) => obj.name === item.searchValue);
      if (index !== -1) {
        item.valuesToRemove.forEach((valueToRemove) => {
          const removeIndex = newArray.findIndex((obj) => obj.name === valueToRemove);
          if (removeIndex !== -1) {
            newArray.splice(removeIndex, 1);
          }
        });
      }
    });
    return newArray;
  };

  const filterSingleRooms = (arr: RoomType[]) => arr.filter((room) => room.capacity !== 1);


  const filteredRoomTypes = useMemo(() => {
    let filtered = [...(availableRoomTypes || [])];

    // Same as the search logic: if there is more than 1 guest, do not offer rooms with capacity = 1
    if (guestsFromBooking !== 1) {
      filtered = filterSingleRooms(filtered);
    }

    filtered = removeSpecificObject(filtered, [
      { searchValue: "Rook Inside", valuesToRemove: ["Rook", "Knight", "Knight Inside"] },
      { searchValue: "Rook", valuesToRemove: ["Knight", "Knight Inside"] },
      { searchValue: "Knight Inside", valuesToRemove: ["Knight"] },
      { searchValue: "Bishop Inside", valuesToRemove: ["Pawn Inside"] },
    ]);

    return filtered;
  }, [availableRoomTypes, guestsFromBooking]);


  useEffect(() => {
    if (noAvailableRooms) {
      setSelectedRoomType(null);
      return;
    }


    // If the currently selected option is still in the list — do not change it
    const selectedStillValid =
      !!selectedRoomType &&
      (
        filteredRoomTypes.some(r => r.roomTypeId === selectedRoomType.roomTypeId) ||
        selectedRoomType.roomTypeId === currentRoomTypeId
      );

    if (selectedStillValid) return;


    // Select by priority, same logic as in the search flow
    const regular = filteredRoomTypes.find(rt =>
      rt.name === 'Rook' ||
      rt.name === 'Knight' ||
      rt.name === 'Rook Inside' ||
      rt.name === 'Knight Inside'
    );

    const queen = filteredRoomTypes.find(rt => rt.name === 'Queen');
    const king = filteredRoomTypes.find(rt => rt.name === 'King');
    const pawn = filteredRoomTypes.find(rt => rt.name === 'Pawn Inside');

    setSelectedRoomType(regular || queen || king || pawn || filteredRoomTypes[0] || null);
  }, [filteredRoomTypes, noAvailableRooms, selectedRoomType, currentRoomTypeId]);

  // if (debug) {
  //   console.log('applyThisChangeToAllRooms:', applyThisChangeToAllRooms);
  //   console.log('selectedRoomType:', selectedRoomType);
  //   console.log('roomId from url:', roomId);
  // }

  const filteredRoomTypesToRender = useMemo(() => {
    // If the current room type exists in the results, remove it from the list
    // because it will be shown as a separate card at the top
    if (!currentRoomTypeId) return filteredRoomTypes;
    return filteredRoomTypes.filter(rt => rt.roomTypeId !== currentRoomTypeId);
  }, [filteredRoomTypes, currentRoomTypeId]);


  if (debug) {
    console.log('(ModifyRoomType) filteredRoomTypesToRender: ', filteredRoomTypesToRender);
  }

  const formatPriceDiff = (value: number) => {
    if (value === 0) return 'no price change';

    const sign = value > 0 ? '+' : '-';
    return `${sign}${Math.abs(value).toFixed(2)}$`;
  };


  const formatMoney = (amount: number) => {
    const n = Number(amount || 0);
    const sign = n < 0 ? "-" : "";
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  };






  useEffect(() => {
    // On first load, preselect current room type
    if (!selectedRoomType && currentRoomTypeAsRoomType) {
      setSelectedRoomType(currentRoomTypeAsRoomType);
    }
  }, [selectedRoomType, currentRoomTypeAsRoomType]);


  // Step inside this single screen
  const [step, setStep] = useState<'ROOM_TYPE' | 'ROOM_OPTIONS'>('ROOM_TYPE');

  // Pairs (same logic as old RoomOptions screen)
  // const roomPairs: Record<string, { noWindow: string; window: string }> = {
  //   'Rook Inside': { noWindow: 'Rook Inside', window: 'Rook' },
  //   'Knight Inside': { noWindow: 'Knight Inside', window: 'Knight' },
  //   'Rook': { noWindow: 'Rook Inside', window: 'Rook' },
  //   'Knight': { noWindow: 'Knight Inside', window: 'Knight' },
  //   'Bishop Inside': { noWindow: 'Bishop Inside', window: 'Pawn Inside' },
  //   'Pawn Inside': { noWindow: 'Bishop Inside', window: 'Pawn Inside' },
  // };

  // *** Now imporded from roomTypeOptions


  const selectedPair = useMemo(() => {
    if (!selectedRoomType) return null;
    return roomPairs[selectedRoomType.name] ?? null;
  }, [selectedRoomType]);

  // Find two sub-variants in the availability list (if any)
  const roomOptions = useMemo(() => {
    if (!selectedPair) return { noWindow: null, window: null };

    const noWindow = (availableRoomTypes || []).find(rt => rt.name === selectedPair.noWindow) ?? null;
    const windowRt = (availableRoomTypes || []).find(rt => rt.name === selectedPair.window) ?? null;

    return { noWindow, window: windowRt };
  }, [availableRoomTypes, selectedPair]);

  const hasRoomOptions = !!(roomOptions.noWindow && roomOptions.window);

  // Selected sub-variant (only used on step ROOM_OPTIONS)
  const [selectedRoomOption, setSelectedRoomOption] = useState<RoomType | null>(null);

  // When we enter ROOM_OPTIONS, default-select "noWindow" (same as old flow)
  useEffect(() => {
    if (step === 'ROOM_OPTIONS') {
      setSelectedRoomOption(roomOptions.noWindow || roomOptions.window || null);
    } else {
      setSelectedRoomOption(null);
    }
  }, [step, roomOptions.noWindow, roomOptions.window]);

  const formatDiffUSD = (value: number) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}$${value.toFixed(2)}`;
  };

  const isCurrentRoomSelected =
    step === 'ROOM_TYPE' &&
    !!selectedRoomType &&
    selectedRoomType.roomTypeId === currentRoomTypeId;









  // if (debug) {
  //   console.log({
  //     selectedName: selectedRoomType?.name,
  //     selectedPair,
  //     noWindow: roomOptions.noWindow?.name,
  //     window: roomOptions.window?.name,
  //     hasRoomOptions,
  //   });
  // }




  // final selected room type for preview (and for submit)
  const finalSelectedRoomType =
    step === 'ROOM_OPTIONS'
      ? (selectedRoomOption ?? selectedRoomType)
      : selectedRoomType



  const isGroupBooking = (trip?.numberOfRooms ?? 1) > 1;

  const finalRoomTypeIdForGroupCheck = finalSelectedRoomType?.roomTypeId ?? null;

  const shouldRunGroupRoomTypeCheck =
    isGroupBooking &&
    !!finalRoomTypeIdForGroupCheck &&
    finalRoomTypeIdForGroupCheck !== currentRoomTypeId &&
    (
      (step === 'ROOM_TYPE' && !hasRoomOptions) ||
      (step === 'ROOM_OPTIONS')
    );

  const {
    canChangeGroupRoomType,
    loading: loadingCanChangeGroupRoomType,
  } = useCanChangeGroupRoomType({
    groupId,
    newRoomTypeId: finalRoomTypeIdForGroupCheck,
    enabled: shouldRunGroupRoomTypeCheck,
  });



  // Should we show "Apply this change to all rooms" checkbox?
  const shouldShowApplyAllRooms = useMemo(() => {
    if (!isGroupBooking) return false;

    if (!finalSelectedRoomType) return false;

    if (finalSelectedRoomType.roomTypeId === currentRoomTypeId) {
      return false;
    }

    if (step === 'ROOM_TYPE' && hasRoomOptions) {
      return false;
    }

    return canChangeGroupRoomType;
  }, [
    isGroupBooking,
    finalSelectedRoomType,
    currentRoomTypeId,
    step,
    hasRoomOptions,
    canChangeGroupRoomType,
  ]);

  // show overview only:
  // - if NO second step → show on step 1
  // - if HAS second step → show only on step 2
  const shouldShowOverview =
    (!hasRoomOptions && step === 'ROOM_TYPE') ||
    (hasRoomOptions && step === 'ROOM_OPTIONS')

  // Reset checkbox if it becomes hidden due to selection change
  useEffect(() => {
    if (!shouldShowApplyAllRooms && applyThisChangeToAllRooms) {
      setApplyThisChangeToAllRooms(false);
    }
  }, [shouldShowApplyAllRooms, applyThisChangeToAllRooms]);

  const preview = useMemo(() => {
    return calcModifyRoomTypePreview({
      trip,
      bookingId,
      applyToAll: applyThisChangeToAllRooms,
      finalSelectedRoomType: finalSelectedRoomType
        ? {
          roomTypeId: finalSelectedRoomType.roomTypeId,
          name: finalSelectedRoomType.name,
          bed: finalSelectedRoomType.bed,
          hasWindows: finalSelectedRoomType.hasWindows,
          capacity: finalSelectedRoomType.capacity,
          pricePerNight: Number(finalSelectedRoomType.pricePerNight ?? 0),
        }
        : null,
    });
  }, [trip, bookingId, applyThisChangeToAllRooms, finalSelectedRoomType]);


  const canShowOverview =
    shouldShowOverview &&
    !!finalSelectedRoomType &&
    !loading &&
    !!booking &&
    !noAvailableRooms &&
    !isCurrentRoomSelected &&
    !!preview;

  const buttonLabel =
    hasRoomOptions
      ? (step === 'ROOM_OPTIONS' ? 'Modify Booking' : 'Continue')
      : 'Modify Booking'


  return (
    <AccountContentContainer>
      <div className="cardContent">

        {loading && (
          <div className="datesText" style={{ marginTop: 12 }}>
            Loading booking...
          </div>
        )}

        {!loading && !booking && (
          <div className="datesText" style={{ marginTop: 12 }}>
            Booking not found.
          </div>
        )}

        {!loading && booking && (
          <>


            <div style={{ marginTop: 12 }}>
              {loadingRoomTypes ? (
                <div className="datesText">Loading available room types...</div>
              ) : noAvailableRooms ? (
                <div className="datesText">No available room types for these dates.</div>
              ) : (

                <>
                  {step === 'ROOM_TYPE' && (

                    <>
                      {currentRoomTypeAsRoomType && (() => {
                        const roomTypeOption = ROOM_TYPE_OPTIONS[currentRoomTypeAsRoomType.name];
                        if (!roomTypeOption) return null;

                        // if (debug) {
                        //   console.log("currentRoomTypeAsRoomType = ", currentRoomTypeAsRoomType);
                        // }
                        return (
                          <>

                            <div className="optionCardsContainer" style={{ marginBottom: 16 }}>
                              <div
                                key={`current-${currentRoomTypeAsRoomType.roomTypeId}`}
                                onClick={() => setSelectedRoomType(currentRoomTypeAsRoomType)}
                              >
                                <OptionCardModifyRoomType
                                  className={currentRoomTypeAsRoomType.name}
                                  title={"Your current booking"}
                                  titleIcon={roomTypeOption.titleIcon}
                                  description={roomTypeOption.description}
                                  attributes={roomTypeOption.attributes}
                                  roomTypeId={currentRoomTypeAsRoomType.roomTypeId}
                                  roomTypeName={currentRoomTypeAsRoomType.name}
                                  pricePerNight={currentRoomTypeAsRoomType.pricePerNight}
                                  bed={currentRoomTypeAsRoomType.bed}
                                  hasWindows={currentRoomTypeAsRoomType.hasWindows}
                                  selectedTitle={true}
                                  isSelected={selectedRoomType?.roomTypeId === currentRoomTypeAsRoomType.roomTypeId}
                                  onSelect={() => setSelectedRoomType(currentRoomTypeAsRoomType)}
                                />
                              </div>
                            </div>
                          </>
                        );
                      })()}


                      <div className="optionCardsContainer">
                        {filteredRoomTypesToRender.map((roomType: RoomType) => {
                          const roomTypeOption = ROOM_TYPE_OPTIONS[roomType.name];
                          if (!roomTypeOption) return null;

                          const priceDiff = roomType.pricePerNight - currentPricePerNight;
                          return (
                            <div
                              key={roomType.roomTypeId}
                              onClick={() => setSelectedRoomType(roomType)}
                            >
                              <OptionCardModifyRoomType
                                className={roomType.name}
                                title={roomTypeOption.title}
                                titleIcon={roomTypeOption.titleIcon}
                                description={roomTypeOption.description}
                                attributes={roomTypeOption.attributes}
                                roomTypeId={roomType.roomTypeId}
                                roomTypeName={roomType.name}
                                pricePerNight={priceDiff}
                                priceLabel={formatPriceDiff(priceDiff)}
                                bed={roomType.bed}
                                hasWindows={roomType.hasWindows}
                                isSelected={selectedRoomType?.roomTypeId === roomType.roomTypeId}
                                onSelect={() => setSelectedRoomType(roomType)}
                              />
                            </div>
                          );
                        })}
                      </div>

                      {/* {selectedRoomType && (
                        <div className="datesText" style={{ marginTop: 12 }}>
                          Selected: <b>{selectedRoomType.name}</b>
                        </div>
                      )} */}

                    </>
                  )}

                  {step === 'ROOM_OPTIONS' && hasRoomOptions && (
                    <>

                      {(() => {
                        const noWindowRoomType = roomOptions.noWindow!;
                        const windowRoomType = roomOptions.window!;

                        const isNoWindowSelected = selectedRoomOption?.roomTypeId === noWindowRoomType.roomTypeId;
                        const isWindowSelected = selectedRoomOption?.roomTypeId === windowRoomType.roomTypeId;

                        const windowPriceDiff = windowRoomType.pricePerNight - noWindowRoomType.pricePerNight;

                        const isBishopInside = noWindowRoomType.name === 'Bishop Inside';
                        const isPawnInsideWindow = windowRoomType.name === 'Pawn Inside';

                        return (
                          <>
                            {/* NO WINDOW / BASE */}
                            <Card selected={isNoWindowSelected} style={{ cursor: 'pointer' }}>
                              <OptionCardContainer
                                selected={isNoWindowSelected}
                                className="optionsCard"
                                onClick={() => setSelectedRoomOption(noWindowRoomType)}
                              >
                                <div className="optionCardTitle">
                                  {!isBishopInside && (
                                    <div className="title">A room with a skylight</div>
                                  )}

                                  {isBishopInside && (
                                    <div className="title">Embrace the darkness</div>
                                  )}

                                  {isBishopInside ? (
                                    <div className="description">
                                      <Vompire />
                                      <span>Perfect for vampires</span>
                                    </div>
                                  ) : (
                                    <div className="description">
                                      <MoneyIcon />
                                      <span>For the frugal minimalist</span>
                                    </div>
                                  )}
                                </div>

                                {!isBishopInside ? (
                                  <div className="optionCardAttributesFeatures">
                                    <div className="roomAttribute">
                                      <GreenCheckIconNew />
                                      Most affordable option
                                    </div>
                                    <div className="roomAttribute">
                                      <GreenCheckIconNew />
                                      Natural light
                                    </div>
                                    <div className="roomAttribute roomAttributeCross">
                                      <RedCloseIcon />
                                      No window
                                    </div>
                                  </div>
                                ) : (
                                  <div className="optionCardAttributesFeatures">
                                    <div className="roomAttribute">
                                      <GreenCheckIconNew />
                                      Great for sleeping during the day
                                    </div>
                                    <div className="roomAttribute">
                                      <GreenCheckIconNew />
                                      Most affordable option
                                    </div>
                                    <div className="roomAttribute roomAttributeCross">
                                      <RedCloseIcon />
                                      No windows or skylight
                                    </div>
                                  </div>
                                )}

                                <div className="windowsOption">
                                  <div className="radioAndPrice">
                                    <div className="customizedRadio"></div>
                                    <div className="optionDescription">
                                      Included in current room price
                                    </div>
                                  </div>
                                </div>
                              </OptionCardContainer>
                            </Card>

                            {/* WINDOW / UPGRADE */}
                            <Card selected={isWindowSelected} style={{ cursor: 'pointer' }}>
                              <OptionCardContainer
                                selected={isWindowSelected}
                                onClick={() => setSelectedRoomOption(windowRoomType)}
                              >
                                {isPawnInsideWindow ? (
                                  <div className="optionCardTitle">
                                    <div className="title">A room with a skylight</div>
                                    <div className="description">
                                      <MoneyIcon />
                                      <span>For the frugal minimalist</span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="optionCardTitle">
                                    <div className="title">A room with a view</div>
                                    <div className="description">
                                      <CityView />
                                      <span>Enjoy the cityscape</span>
                                    </div>
                                  </div>
                                )}

                                {!isPawnInsideWindow ? (
                                  <div className="optionCardAttributesFeatures">
                                    <div className="roomAttribute">
                                      <GreenCheckIconNew />
                                      Large windows with view of downtown
                                    </div>
                                  </div>
                                ) : (
                                  <div className="optionCardAttributesFeatures">
                                    <div className="roomAttribute">
                                      <GreenCheckIconNew />
                                      Most affordable option
                                    </div>
                                    <div className="roomAttribute">
                                      <GreenCheckIconNew />
                                      Natural light
                                    </div>
                                    <div className="roomAttribute roomAttributeCross">
                                      <RedCloseIcon />
                                      No window
                                    </div>
                                  </div>
                                )}

                                <div className="windowsOption">
                                  <div className="radioAndPrice">
                                    <div className="customizedRadio"></div>
                                    <div className="optionDescription">
                                      {formatDiffUSD(windowPriceDiff)}
                                    </div>
                                  </div>
                                  <div className="explanation">
                                    per night, not including tax / fees
                                  </div>
                                </div>
                              </OptionCardContainer>
                            </Card>
                          </>
                        );
                      })()}
                    </>
                  )}

                  {!loadingCanChangeGroupRoomType && shouldShowApplyAllRooms && (
                    <div className="applyChanges">
                      <Checkbox
                        className="accountCheckbox"
                        checked={applyThisChangeToAllRooms}
                        borderColor={colors.noBookingText}
                        label="Apply this change to all rooms"
                        onChange={() => setApplyThisChangeToAllRooms(prev => !prev)}
                        checkboxScale={1.2}
                      />
                    </div>
                  )}
                  <div className="changeNote">
                    All rooms include access to our state-of-the-art fitness facility, sauna and steam room. Our hotel also features a cafe and a restaurant with options for all types of dietary needs (vegan, keto, gluten free).
                  </div>

                </>

              )}
            </div>



            {canShowOverview && preview && (
              <div className="bookingChanges">
                <div className="bookingOverview">OVERVIEW</div>

                {preview.previewLines.map((x) => (
                  <div className="bookingOverviewLine" key={x.key}>
                    <div className={`bookingOverviewTitle ${(x.red || x.isModified) ? "priceAdd" : ""}`}>
                      <div className="bookingNameAndPrice">{x.title}</div>
                    </div>

                    <div className="bookingOverviewDots">
                      <div className="dashedLine"></div>
                    </div>
                    <div className={`bookingOverviewNumber ${(x.red || x.isModified) ? "priceAdd" : ""}`}>
                      {formatMoney(x.amount)}
                    </div>
                  </div>
                ))}

                <div className="bookingOverviewTotal">
                  <div className="bookingOverviewTotalTitle">YOUR NEW TOTAL</div>
                  <div className="bookingOverviewDots"></div>
                  <div className="bookingOverviewNumber">
                    {formatMoney(preview.preview.total)}
                  </div>
                </div>
              </div>
            )}



            <div className="buttonContainer">
              <div
                className="cancelBt"
                onClick={() => {
                  if (step === 'ROOM_OPTIONS') {
                    setStep('ROOM_TYPE');
                    return;
                  }
                  handleCancel();
                }}
              >
                {step === 'ROOM_OPTIONS' ? 'Back' : 'Cancel'}
              </div>
              <StyledButton
                width="140px"
                height="48px"
                onClick={handleRoomTypeUpdate}
                disabled={isCurrentRoomSelected || changingRoomType}
              >
                {buttonLabel}
              </StyledButton>
            </div>
          </>
        )}
      </div>
    </AccountContentContainer>
  );
};

export default ModifyRoomType;